import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CarTaxiFront, UtensilsCrossed, Star, Loader2, Power, MapPin, XCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Lift — player-run rideshare & food delivery. Riders post, on-duty drivers claim,
   fares move bank-to-bank on completion (Lift takes 10%), riders rate drivers. */

const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();
const DEMO_BOARD = {
  duty: false, rating: 4.8, rating_n: 23,
  open: [
    { id: "lr1", kind: "ride", rider_name: "Ada King", pickup: "Night Market", dropoff: "Skyline Tower", note: "in a hurry", fare: 210, surge: 1.25, status: "open", created_at: iso(6) },
    { id: "lr2", kind: "eats", rider_name: "Ray Dalton", pickup: "Taco Libre", dropoff: "Bornwood Docks", note: "extra hot sauce", fare: 68, surge: 1, status: "open", created_at: iso(14) },
  ],
  mine: [{ id: "lr3", kind: "ride", pickup: "City Hall", dropoff: "Pillbox General", fare: 155, surge: 1, status: "claimed", driver_name: "T. Vercetti", created_at: iso(22) }],
  driving: [],
};

export default function LiftApp({ demo, serverId, token, accent, sfx }) {
  const [board, setBoard] = useState(demo ? DEMO_BOARD : null);
  const [kind, setKind] = useState("ride");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/ride/board`, { params: { token } })
      .then((r) => setBoard(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => {
    load();
    if (!demo) { const t = setInterval(load, 8000); return () => clearInterval(t); }
  }, [load, demo]);

  const act = async (key, fn) => {
    if (busy) return;
    setBusy(key); sfx?.keyTick();
    try {
      const r = await fn();
      toast.success(r.data.message);
      if (demo) return;
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const request = () => act("req", async () => {
    if (demo) { toast.success(`Preview: ${kind} posted from ${pickup || "City Hall"} (demo)`); return { data: {} }; }
    const r = await api.post(`/nc/servers/${serverId}/ride/request`, { kind, pickup, dropoff, note: "" }, { params: { token } });
    setPickup(""); setDropoff("");
    return r;
  });

  const toggleDuty = () => act("duty", async () => {
    if (demo) { setBoard((b) => ({ ...b, duty: !b.duty })); return { data: {} }; }
    return api.post(`/nc/servers/${serverId}/ride/duty`, { on: !board?.duty }, { params: { token } });
  });

  if (!board) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-white/50" /></div>;

  const Row = ({ r, actions }) => (
    <div data-testid={`lift-row-${r.id}`} className="border border-white/10 bg-white/[0.03] rounded-2xl px-3.5 py-3">
      <div className="flex items-center gap-2">
        {r.kind === "ride" ? <CarTaxiFront className="w-4 h-4 text-yellow-400 shrink-0" /> : <UtensilsCrossed className="w-4 h-4 text-orange-400 shrink-0" />}
        <p className="font-mono text-[10px] text-white flex-1 truncate">{r.pickup} → {r.dropoff || "kitchen drop"}</p>
        <span className="font-mono text-[10px] font-bold text-emerald-400 shrink-0">${r.fare}</span>
      </div>
      <p className="font-mono text-[8px] text-white/40 mt-1 uppercase tracking-[0.15em]">
        {r.rider_name || r.driver_name} {r.surge > 1 ? `· surge x${r.surge}` : ""} · {r.status}</p>
      {actions}
    </div>
  );

  return (
    <div data-testid="lift-app" className="flex-1 flex flex-col gap-3 overflow-y-auto pb-4" style={{ scrollbarWidth: "none" }}>
      <div className="flex items-center gap-2">
        <p className="font-sans font-bold text-white text-lg flex-1">Lift</p>
        {board.rating != null && <span className="font-mono text-[9px] text-yellow-400 flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-400" /> {board.rating} ({board.rating_n})</span>}
        <button data-testid="lift-duty-toggle" onClick={toggleDuty}
          className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${board.duty ? "border-emerald-400/60 text-emerald-400 bg-emerald-400/10" : "border-white/15 text-white/50"}`}>
          <Power className="w-3 h-3" /> {board.duty ? "On duty" : "Go on duty"}
        </button>
      </div>

      {/* request */}
      <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-3.5 flex flex-col gap-2.5">
        <div className="flex gap-2">
          {[["ride", CarTaxiFront, "Ride"], ["eats", UtensilsCrossed, "Eats"]].map(([id, Icon, label]) => (
            <button key={id} data-testid={`lift-kind-${id}`} onClick={() => { setKind(id); sfx?.keyTick(); }}
              className={`flex-1 py-2 rounded-xl font-mono text-[9px] uppercase tracking-[0.15em] border transition-colors flex items-center justify-center gap-1.5 ${kind === id ? "border-yellow-400/60 text-yellow-300 bg-yellow-400/10" : "border-white/10 text-white/50"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border border-white/10 rounded-xl px-3 py-2.5 bg-black/30">
          <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <input data-testid="lift-pickup" value={pickup} onChange={(e) => setPickup(e.target.value)} maxLength={60} placeholder={kind === "ride" ? "Pickup spot" : "Restaurant"}
            className="flex-1 bg-transparent font-mono text-[11px] text-white placeholder:text-white/30 focus:outline-none" />
        </div>
        <div className="flex items-center gap-2 border border-white/10 rounded-xl px-3 py-2.5 bg-black/30">
          <MapPin className="w-3.5 h-3.5 text-red-400 shrink-0" />
          <input data-testid="lift-dropoff" value={dropoff} onChange={(e) => setDropoff(e.target.value)} maxLength={60} placeholder="Drop-off"
            className="flex-1 bg-transparent font-mono text-[11px] text-white placeholder:text-white/30 focus:outline-none" />
        </div>
        <button data-testid="lift-request" disabled={busy === "req" || pickup.trim().length < 2} onClick={request}
          className="w-full py-3 rounded-xl font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-black transition-colors disabled:opacity-40" style={{ backgroundColor: accent }}>
          {busy === "req" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Request ${kind}`}
        </button>
      </div>

      {/* my active */}
      {board.mine.filter((m) => ["open", "claimed", "done"].includes(m.status)).map((m) => (
        <Row key={m.id} r={m} actions={
          <div className="flex gap-2 mt-2">
            {m.status === "open" && <button data-testid={`lift-cancel-${m.id}`} onClick={() => act(`c${m.id}`, () => api.post(`/nc/servers/${serverId}/ride/${m.id}/cancel`, {}, { params: { token } }))}
              className="font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-lg border border-red-400/40 text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancel</button>}
            {m.status === "claimed" && <p className="font-mono text-[8px] text-sky-400 uppercase tracking-[0.15em] py-1.5">{m.driver_name} is on the way</p>}
            {m.status === "done" && !m.rating && (
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} data-testid={`lift-rate-${m.id}-${s}`} onClick={() => act(`r${m.id}`, () => api.post(`/nc/servers/${serverId}/ride/${m.id}/rate`, { stars: s }, { params: { token } }))}>
                    <Star className="w-4 h-4 text-yellow-400 hover:fill-yellow-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        } />
      ))}

      {/* driver board */}
      {board.driving.map((m) => (
        <Row key={m.id} r={m} actions={
          <button data-testid={`lift-complete-${m.id}`} onClick={() => act(`d${m.id}`, () => api.post(`/nc/servers/${serverId}/ride/${m.id}/complete`, {}, { params: { token } }))}
            className="mt-2 w-full py-2 rounded-xl bg-emerald-500/90 font-mono text-[9px] uppercase tracking-[0.2em] font-bold text-black flex items-center justify-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Complete — bank ${Math.floor(m.fare * 0.9)}
          </button>
        } />
      ))}
      {board.open.length > 0 && <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40 mt-1">Open requests</p>}
      {board.open.map((o) => (
        <Row key={o.id} r={o} actions={board.duty ? (
          <button data-testid={`lift-claim-${o.id}`} onClick={() => act(`cl${o.id}`, () => api.post(`/nc/servers/${serverId}/ride/${o.id}/claim`, {}, { params: { token } }))}
            className="mt-2 w-full py-2 rounded-xl border font-mono text-[9px] uppercase tracking-[0.2em] font-bold transition-colors"
            style={{ borderColor: `${accent}66`, color: accent }}>Claim ${o.fare}</button>
        ) : null} />
      ))}
      {board.open.length === 0 && <p className="font-mono text-[9px] text-white/30 text-center py-3">No open requests — the city is quiet.</p>}
    </div>
  );
}
